import React, { useState, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Calendar, Heart, Clock, MapPin, Mic, MicOff, Edit, Sparkles, Brain } from "lucide-react";
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { useVoiceDictation } from '@/hooks/useVoiceDictation';
import { ReflectPhase } from './rmethod/ReflectPhase';
import { ReleasePhase } from './rmethod/ReleasePhase';
import { ReprogrammePhase } from './rmethod/ReprogrammePhase';
import { EditableText } from '@/hooks/useEditableContent';

interface PastSelfTimelineProps {
  user: User;
}

interface TimelineEvent {
  id?: string;
  user_id: string;
  age_at_event: number;
  event_date?: string;
  event_title: string;
  event_description: string;
  life_area: string;
  emotional_impact_then?: number;
  emotional_impact_now?: number;
  levels_affected?: any;
  reflection_notes?: string;
  release_notes?: string;
  reprogramme_notes?: string;
}

const LIFE_AREA_LABEL_COLORS: Record<string, string> = {
  relationships: 'bg-pink-500/20 text-pink-300',
  family: 'bg-blue-500/20 text-blue-300',
  career: 'bg-purple-500/20 text-purple-300',
  health: 'bg-green-500/20 text-green-300',
  identity: 'bg-amber-500/20 text-amber-300',
  trauma: 'bg-red-500/20 text-red-300',
  default: 'bg-gray-500/20 text-gray-300',
};

export const PastSelfTimeline: React.FC<PastSelfTimelineProps> = ({ user }) => {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showCustomArea, setShowCustomArea] = useState(false);
  const [customAreaInput, setCustomAreaInput] = useState('');
  const [savedCustomAreas, setSavedCustomAreas] = useState<string[]>([]);
  const [editingEvent, setEditingEvent] = useState<TimelineEvent | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
  const [activeField, setActiveField] = useState<'title' | 'description' | null>(null);
  const [activePhase, setActivePhase] = useState<'reflect' | 'release' | 'reprogramme' | null>(null);
  const [formData, setFormData] = useState<Partial<TimelineEvent>>({
    user_id: user.id,
    age_at_event: 0,
    event_title: '',
    event_description: '',
    life_area: '',
  });
  const { toast } = useToast();
  const { isListening, transcript, startListening, stopListening, resetTranscript } = useVoiceDictation();

  useEffect(() => {
    loadEvents();
  }, [user.id]);

  useEffect(() => {
    if (transcript && activeField) {
      setFormData(prev => ({
        ...prev,
        [activeField === 'title' ? 'event_title' : 'event_description']: 
          (activeField === 'title' ? prev.event_title : prev.event_description) + ' ' + transcript
      }));
      resetTranscript();
    }
  }, [transcript, activeField, resetTranscript]);

  const loadEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('past_self_timeline_events')
        .select('*')
        .eq('user_id', user.id)
        .order('age_at_event', { ascending: true });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error loading timeline events:', error);
      toast({
        title: "Error",
        description: "Failed to load timeline events",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.age_at_event || !formData.event_title || !formData.event_description || !formData.life_area) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      const eventData = {
        user_id: user.id,
        age_at_event: formData.age_at_event,
        event_title: formData.event_title,
        event_description: formData.event_description,
        life_area: formData.life_area,
        event_date: formData.event_date || null,
      };

      if (editingEvent) {
        const { error } = await supabase
          .from('past_self_timeline_events')
          .update(eventData)
          .eq('id', editingEvent.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Timeline event updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('past_self_timeline_events')
          .insert([eventData]);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Timeline event added successfully",
        });
      }

      loadEvents();
      setShowDialog(false);
      setEditingEvent(null);
      setShowCustomArea(false);
      setCustomAreaInput('');
      setFormData({
        user_id: user.id,
        age_at_event: 0,
        event_title: '',
        event_description: '',
        life_area: '',
      });
    } catch (error) {
      console.error('Error saving event:', error);
      toast({
        title: "Error",
        description: "Failed to save timeline event",
        variant: "destructive",
      });
    }
  };

  const handleEditEvent = (event: TimelineEvent) => {
    setEditingEvent(event);
    setFormData({
      user_id: user.id,
      age_at_event: event.age_at_event,
      event_title: event.event_title,
      event_description: event.event_description,
      life_area: event.life_area,
      event_date: event.event_date,
    });
    setShowDialog(true);
  };

  const handleVoiceToggle = () => {
    if (isListening) {
      stopListening();
      setActiveField(null);
    } else {
      setActiveField('description');
      startListening();
    }
  };

  const getLifeAreaLabelColor = (lifeArea: string): string => {
    return LIFE_AREA_LABEL_COLORS[lifeArea.toLowerCase()] || LIFE_AREA_LABEL_COLORS.default;
  };

  const handleSavePhase = async (data: any) => {
    try {
      if (!selectedEvent?.id) return;
      
      const { error } = await supabase
        .from('past_self_timeline_events')
        .update(data)
        .eq('id', selectedEvent.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Your work has been saved",
      });
      
      // Refresh events
      loadEvents();
    } catch (error) {
      console.error('Error saving phase:', error);
      toast({
        title: "Error",
        description: "Failed to save",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-therapeutic shadow-therapy border-0">
        <div className="mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-light text-foreground mb-2">
                <EditableText 
                  contentKey="sanctuary.timeline.title"
                  defaultContent="Past Self Timeline"
                  category="sanctuary"
                  description="Main title for the Past Self Timeline tool"
                />
              </h2>
              <p className="text-muted-foreground mb-4">
                <EditableText 
                  contentKey="sanctuary.timeline.description"
                  defaultContent="Map pivotal moments from your past to understand how they shape your present. Using the R-Method (Recognise, Reflect, Release, Re-programme), we'll transform old patterns into new possibilities."
                  category="sanctuary"
                  description="Description of the Past Self Timeline tool"
                />
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingEvent(null);
                setFormData({
                  user_id: user.id,
                  age_at_event: 0,
                  event_title: '',
                  event_description: '',
                  life_area: '',
                });
                setShowDialog(true);
              }}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              <EditableText 
                contentKey="sanctuary.timeline.add_event_button"
                defaultContent="Add Event"
                category="sanctuary"
                description="Button text to add a new timeline event"
              />
            </Button>
          </div>

          {/* Recognise Phase Section */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary" />
              <EditableText 
                contentKey="sanctuary.timeline.recognise_phase_title"
                defaultContent="Recognise Phase"
                category="sanctuary"
                description="Title for the Recognise phase section"
              />
            </h3>
            <p className="text-sm text-muted-foreground">
              <EditableText 
                contentKey="sanctuary.timeline.recognise_phase_description"
                defaultContent="When we don't recognise what we carry from the past, these experiences become unconscious drivers and programmes that impact our present and get projected onto our future. This process enables us to consciously plot those pivotal moments in our lives that shape us in the present, so we can release the charge and reprocess that past to consciously create and construct the future life we choose."
                category="sanctuary"
                description="Explanation of the Recognise phase purpose"
              />
            </p>
          </div>
        </div>

        {events.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">
              <EditableText 
                contentKey="sanctuary.timeline.empty_state_text"
                defaultContent="No timeline events yet. Add your first pivotal moment to begin."
                category="sanctuary"
                description="Message shown when there are no timeline events"
              />
            </p>
            <Button
              onClick={() => setShowDialog(true)}
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              <EditableText 
                contentKey="sanctuary.timeline.add_first_event_button"
                defaultContent="Add Your First Event"
                category="sanctuary"
                description="Button text for adding first event"
              />
            </Button>
          </div>
        ) : (
          <>
            <div className="bg-background/50 rounded-lg p-6 border-2 border-primary/20">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                    <Heart className="h-5 w-5 text-primary" />
                    <EditableText 
                      contentKey="sanctuary.timeline.framework_title"
                      defaultContent="The R-Method Framework"
                      category="sanctuary"
                      description="Title for R-Method framework section"
                    />
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    <EditableText 
                      contentKey="sanctuary.timeline.framework_description"
                      defaultContent="Transform your past experiences through three evidence-based phases. Select a transformation phase below, then choose an event to begin deep work."
                      category="sanctuary"
                      description="Description of the R-Method framework"
                    />
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button
                    onClick={() => {
                      setActivePhase('reflect');
                      toast({
                        title: "Reflect Mode Active",
                        description: "Select an event from your timeline to begin deep pattern analysis",
                      });
                    }}
                    className={`h-auto py-6 flex flex-col items-center gap-2 transition-all ${
                      activePhase === 'reflect' 
                        ? 'bg-primary text-primary-foreground shadow-lg scale-105 border-primary' 
                        : ''
                    }`}
                    variant={activePhase === 'reflect' ? 'default' : 'outline'}
                  >
                    <Sparkles className="h-8 w-8" />
                    <span className="font-medium">
                      <EditableText 
                        contentKey="sanctuary.timeline.reflect_button"
                        defaultContent="Reflect"
                        category="sanctuary"
                        description="Button text for Reflect phase"
                      />
                    </span>
                    <span className="text-xs opacity-80">
                      <EditableText 
                        contentKey="sanctuary.timeline.reflect_subtitle"
                        defaultContent="Deep pattern analysis"
                        category="sanctuary"
                        description="Subtitle for Reflect phase"
                      />
                    </span>
                    {activePhase === 'reflect' && (
                      <span className="text-xs font-semibold mt-1">
                        <EditableText 
                          contentKey="sanctuary.timeline.active_indicator"
                          defaultContent="● ACTIVE"
                          category="sanctuary"
                          description="Active phase indicator text"
                        />
                      </span>
                    )}
                  </Button>

                  <Button
                    onClick={() => {
                      setActivePhase('release');
                      toast({
                        title: "Release Mode Active",
                        description: "Select an event from your timeline to begin somatic & emotional release work",
                      });
                    }}
                    className={`h-auto py-6 flex flex-col items-center gap-2 transition-all ${
                      activePhase === 'release' 
                        ? 'bg-primary text-primary-foreground shadow-lg scale-105 border-primary' 
                        : ''
                    }`}
                    variant={activePhase === 'release' ? 'default' : 'outline'}
                  >
                    <Heart className="h-8 w-8" />
                    <span className="font-medium">
                      <EditableText 
                        contentKey="sanctuary.timeline.release_button"
                        defaultContent="Release"
                        category="sanctuary"
                        description="Button text for Release phase"
                      />
                    </span>
                    <span className="text-xs opacity-80">
                      <EditableText 
                        contentKey="sanctuary.timeline.release_subtitle"
                        defaultContent="Somatic & emotional release"
                        category="sanctuary"
                        description="Subtitle for Release phase"
                      />
                    </span>
                    {activePhase === 'release' && (
                      <span className="text-xs font-semibold mt-1">
                        <EditableText 
                          contentKey="sanctuary.timeline.active_indicator"
                          defaultContent="● ACTIVE"
                          category="sanctuary"
                          description="Active phase indicator text"
                        />
                      </span>
                    )}
                  </Button>

                  <Button
                    onClick={() => {
                      setActivePhase('reprogramme');
                      toast({
                        title: "Re-programme Mode Active",
                        description: "Select an event from your timeline to begin neural rewiring protocols",
                      });
                    }}
                    className={`h-auto py-6 flex flex-col items-center gap-2 transition-all ${
                      activePhase === 'reprogramme' 
                        ? 'bg-primary text-primary-foreground shadow-lg scale-105 border-primary' 
                        : ''
                    }`}
                    variant={activePhase === 'reprogramme' ? 'default' : 'outline'}
                  >
                    <Sparkles className="h-8 w-8" />
                    <span className="font-medium">
                      <EditableText 
                        contentKey="sanctuary.timeline.reprogramme_button"
                        defaultContent="Re-programme"
                        category="sanctuary"
                        description="Button text for Re-programme phase"
                      />
                    </span>
                    <span className="text-xs opacity-80">
                      <EditableText 
                        contentKey="sanctuary.timeline.reprogramme_subtitle"
                        defaultContent="Neural rewiring protocols"
                        category="sanctuary"
                        description="Subtitle for Re-programme phase"
                      />
                    </span>
                    {activePhase === 'reprogramme' && (
                      <span className="text-xs font-semibold mt-1">
                        <EditableText 
                          contentKey="sanctuary.timeline.active_indicator"
                          defaultContent="● ACTIVE"
                          category="sanctuary"
                          description="Active phase indicator text"
                        />
                      </span>
                    )}
                  </Button>
                </div>

                {activePhase && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="flex items-center justify-between p-3 bg-primary/10 rounded-lg border border-primary/30"
                  >
                    <span className="text-sm font-medium text-primary">
                      {activePhase.charAt(0).toUpperCase() + activePhase.slice(1)} mode active - click an event below to begin
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActivePhase(null)}
                      className="text-primary hover:text-primary"
                    >
                      <EditableText 
                        contentKey="sanctuary.timeline.clear_selection_button"
                        defaultContent="Clear Selection"
                        category="sanctuary"
                        description="Button text to clear phase selection"
                      />
                    </Button>
                  </motion.div>
                )}
              </div>
            </div>

            <div className="relative space-y-8">
              <AnimatePresence>
                {events.map((event, index) => (
                  <div key={event.id} className="relative">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="relative z-10"
                    >
                      <Card 
                        className={`p-4 hover:shadow-lg transition-all cursor-pointer bg-gradient-therapeutic ${
                          selectedEvent?.id === event.id 
                            ? 'border-primary ring-2 ring-primary/50' 
                            : 'border-primary/20'
                        }`}
                        onClick={() => {
                          if (activePhase) {
                            setSelectedEvent(event);
                          } else {
                            toast({
                              title: "Select a Phase First",
                              description: "Choose Reflect, Release, or Re-programme from the framework menu above",
                            });
                          }
                        }}
                      >
                        <div className="flex items-start gap-4">
                          <motion.div 
                            className="flex-shrink-0 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center"
                            whileHover={{ scale: 1.1, rotate: 360 }}
                            transition={{ duration: 0.5 }}
                          >
                            <Clock className="h-8 w-8 text-primary" />
                          </motion.div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-foreground">{event.event_title}</h3>
                                <span className="text-sm text-muted-foreground">• Age {event.age_at_event}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {selectedEvent?.id === event.id && activePhase && (
                                  <span className="text-xs text-primary font-medium">
                                    Working in {activePhase}
                                  </span>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditEvent(event);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{event.event_description}</p>
                            <div className="flex items-center gap-4 text-xs">
                              <span className={`flex items-center gap-1 px-2 py-1 rounded-full ${getLifeAreaLabelColor(event.life_area)}`}>
                                <MapPin className="h-3 w-3" />
                                {event.life_area}
                              </span>
                              {event.event_date && (
                                <span className="text-muted-foreground">{new Date(event.event_date).toLocaleDateString()}</span>
                              )}
                            </div>
                            
                              {/* Quick phase access buttons */}
                            <div className="mt-3 flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActivePhase('reflect');
                                  setSelectedEvent(event);
                                }}
                              >
                                <Brain className="h-3 w-3 mr-1" />
                                <EditableText 
                                  contentKey="sanctuary.timeline.reflect_button"
                                  defaultContent="Reflect"
                                  category="sanctuary"
                                  description="Button text for Reflect phase"
                                />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActivePhase('release');
                                  setSelectedEvent(event);
                                }}
                              >
                                <Heart className="h-3 w-3 mr-1" />
                                <EditableText 
                                  contentKey="sanctuary.timeline.release_button"
                                  defaultContent="Release"
                                  category="sanctuary"
                                  description="Button text for Release phase"
                                />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActivePhase('reprogramme');
                                  setSelectedEvent(event);
                                }}
                              >
                                <Sparkles className="h-3 w-3 mr-1" />
                                  <EditableText 
                                    contentKey="sanctuary.timeline.reprogramme_button"
                                    defaultContent="Re-programme"
                                    category="sanctuary"
                                    description="Button text for Re-programme phase"
                                />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                    
                    {/* Connecting line to next event */}
                    {index < events.length - 1 && (
                      <motion.div
                        className="absolute left-1/2 -translate-x-1/2 top-full w-0.5 h-8 bg-gradient-to-b from-primary via-primary/50 to-primary/30 z-0"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 32, opacity: 1 }}
                        transition={{ duration: 0.6, delay: (index + 1) * 0.1 }}
                      >
                        {/* Pulsating glow effect */}
                        <motion.div
                          className="absolute inset-0 bg-primary/30 blur-sm"
                          animate={{ 
                            opacity: [0.3, 0.7, 0.3],
                            scale: [1, 1.2, 1]
                          }}
                          transition={{ 
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        />
                        {/* Animated traveling dot */}
                        <motion.div
                          className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-primary rounded-full shadow-[0_0_12px_rgba(203,178,129,1)] z-10"
                          animate={{ y: [0, 32] }}
                          transition={{ 
                            duration: 2,
                            repeat: Infinity,
                            ease: "linear"
                          }}
                        />
                      </motion.div>
                    )}
                  </div>
                ))}
              </AnimatePresence>
            </div>
          </>
        )}
      </Card>

      {selectedEvent && activePhase === 'reflect' && (
        <ReflectPhase
          isOpen={true}
          onClose={() => {
            setSelectedEvent(null);
            setActivePhase(null);
          }}
          onSave={handleSavePhase}
          eventTitle={selectedEvent.event_title}
        />
      )}

      {selectedEvent && activePhase === 'release' && (
        <ReleasePhase
          isOpen={true}
          onClose={() => {
            setSelectedEvent(null);
            setActivePhase(null);
          }}
          onSave={handleSavePhase}
          eventTitle={selectedEvent.event_title}
        />
      )}

      {selectedEvent && activePhase === 'reprogramme' && (
        <ReprogrammePhase
          isOpen={true}
          onClose={() => {
            setSelectedEvent(null);
            setActivePhase(null);
          }}
          onSave={handleSavePhase}
          eventTitle={selectedEvent.event_title}
        />
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              <EditableText 
                contentKey="sanctuary.timeline.dialog_title"
                defaultContent={editingEvent ? 'Edit Timeline Event' : 'Add Timeline Event'}
                category="sanctuary"
                description="Dialog title for adding/editing timeline event"
              />
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="age">
                <EditableText 
                  contentKey="sanctuary.timeline.age_label"
                  defaultContent="Age at Event *"
                  category="sanctuary"
                  description="Label for age field"
                />
              </Label>
              <Input
                id="age"
                type="number"
                value={formData.age_at_event || ''}
                onChange={(e) => setFormData({ ...formData, age_at_event: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="title">
                <EditableText 
                  contentKey="sanctuary.timeline.title_label"
                  defaultContent="Event Title *"
                  category="sanctuary"
                  description="Label for event title field"
                />
              </Label>
              <Input
                id="title"
                value={formData.event_title || ''}
                onChange={(e) => setFormData({ ...formData, event_title: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="description">
                <EditableText 
                  contentKey="sanctuary.timeline.description_label"
                  defaultContent="Description *"
                  category="sanctuary"
                  description="Label for description field"
                />
              </Label>
              <div className="space-y-2">
                <Textarea
                  id="description"
                  value={formData.event_description || ''}
                  onChange={(e) => setFormData({ ...formData, event_description: e.target.value })}
                  rows={4}
                />
                <Button
                  type="button"
                  variant={isListening && activeField === 'description' ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleVoiceToggle()}
                  className="w-full"
                >
                  {isListening && activeField === 'description' ? (
                    <>
                      <MicOff className="h-4 w-4 mr-2" />
                      <EditableText 
                        contentKey="sanctuary.timeline.stop_dictation"
                        defaultContent="Stop Dictation"
                        category="sanctuary"
                        description="Button text to stop voice dictation"
                      />
                    </>
                  ) : (
                    <>
                      <Mic className="h-4 w-4 mr-2" />
                      <EditableText 
                        contentKey="sanctuary.timeline.dictate_button"
                        defaultContent="Dictate Title or Description"
                        category="sanctuary"
                        description="Button text to start voice dictation"
                      />
                    </>
                  )}
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="life_area" className="text-foreground/90">
                <EditableText 
                  contentKey="sanctuary.timeline.life_area_label"
                  defaultContent="Life Area *"
                  category="sanctuary"
                  description="Label for life area field"
                />
              </Label>
              <Select 
                value={formData.life_area || ''} 
                onValueChange={(value) => {
                  if (value === 'other') {
                    setShowCustomArea(true);
                    setFormData({ ...formData, life_area: '' });
                  } else {
                    setShowCustomArea(false);
                    setFormData({ ...formData, life_area: value });
                  }
                }}
              >
                <SelectTrigger className="w-full bg-background text-foreground">
                  <SelectValue placeholder="Select a life area" className="text-foreground" />
                </SelectTrigger>
                <SelectContent className="bg-background border-border">
                  <SelectItem value="relationships" className="text-foreground hover:bg-muted">Relationships</SelectItem>
                  <SelectItem value="family" className="text-foreground hover:bg-muted">Family</SelectItem>
                  <SelectItem value="career" className="text-foreground hover:bg-muted">Career/Education</SelectItem>
                  <SelectItem value="health" className="text-foreground hover:bg-muted">Health</SelectItem>
                  <SelectItem value="identity" className="text-foreground hover:bg-muted">Identity/Self</SelectItem>
                  <SelectItem value="trauma" className="text-foreground hover:bg-muted">Trauma/Loss</SelectItem>
                  {savedCustomAreas.map((area) => (
                    <SelectItem key={area} value={area} className="text-foreground hover:bg-muted">{area}</SelectItem>
                  ))}
                  <SelectItem value="other" className="text-foreground hover:bg-muted">Other (specify below)</SelectItem>
                </SelectContent>
              </Select>
              
              {showCustomArea && (
                <div className="mt-3 space-y-2">
                  <Label htmlFor="custom_area" className="text-foreground/90">Specify Life Area *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="custom_area"
                      placeholder="e.g., Creative Expression, Spirituality..."
                      value={customAreaInput}
                      onChange={(e) => setCustomAreaInput(e.target.value)}
                      className="flex-1 bg-background text-foreground"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && customAreaInput.trim()) {
                          e.preventDefault();
                          setFormData({ ...formData, life_area: customAreaInput.trim() });
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (customAreaInput.trim()) {
                          setFormData({ ...formData, life_area: customAreaInput.trim() });
                          if (!savedCustomAreas.includes(customAreaInput.trim())) {
                            setSavedCustomAreas([...savedCustomAreas, customAreaInput.trim()]);
                            toast({
                              title: "Custom area saved",
                              description: "This area will now appear in your dropdown list",
                            });
                          }
                        }
                      }}
                      disabled={!customAreaInput.trim()}
                    >
                      Save
                    </Button>
                  </div>
                  {formData.life_area && formData.life_area !== 'other' && (
                    <p className="text-sm text-primary">Selected: {formData.life_area}</p>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="date">
                <EditableText 
                  contentKey="sanctuary.timeline.date_label"
                  defaultContent="Event Date (optional)"
                  category="sanctuary"
                  description="Label for event date field"
                />
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.event_date || ''}
                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowDialog(false);
                setEditingEvent(null);
                if (isListening) stopListening();
              }}>
                <EditableText 
                  contentKey="sanctuary.timeline.cancel_button"
                  defaultContent="Cancel"
                  category="sanctuary"
                  description="Button text to cancel dialog"
                />
              </Button>
              <Button onClick={handleSubmit}>
                <EditableText 
                  contentKey="sanctuary.timeline.submit_button"
                  defaultContent={editingEvent ? 'Update Event' : 'Add Event'}
                  category="sanctuary"
                  description="Button text to save event"
                />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
